import React from 'react';
import styled, { keyframes, css } from 'styled-components';

const BRAND = '#082189';

/* ── Spinner ── */
const ccSpin = keyframes`to{transform:rotate(360deg)}`;

const SpinnerSpan = styled.span`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: inline-block;
  border-radius: 50%;
  border: ${({ $size }) => Math.max(2, $size / 10)}px solid rgba(255, 255, 255, 0.35);
  border-top-color: ${({ $color }) => $color};
  animation: ${ccSpin} 0.7s linear infinite;
`;

export const Spinner = ({ size = 20, color = '#fff' }) => (
  <SpinnerSpan $size={size} $color={color} />
);

/* ── Button ── */
const VARIANTS = {
  primary:  css`background: ${BRAND}; color: #fff;`,
  dark:     css`background: #0f172a; color: #fff;`,
  outline:  css`background: #fff; color: ${BRAND}; border: 2px solid ${BRAND};`,
  ghost:    css`background: #f3f4f6; color: #111827;`,
  danger:   css`background: #fff; color: #dc2626; border: 2px solid #fecaca;`,
};

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border: none;
  transition: opacity 0.15s, transform 0.12s;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  font-family: inherit;
  white-space: nowrap;
  ${({ $variant }) => VARIANTS[$variant] || VARIANTS.primary}
`;

export const Button = ({ children, variant = 'primary', loading = false, disabled, style, ...rest }) => {
  const isDisabled = disabled || loading;
  return (
    <StyledButton
      $variant={variant}
      $disabled={isDisabled}
      disabled={isDisabled}
      style={style}
      {...rest}
    >
      {loading ? <Spinner size={16} color={variant === 'outline' || variant === 'ghost' || variant === 'danger' ? BRAND : '#fff'} /> : null}
      {children}
    </StyledButton>
  );
};

/* ── Input ── */
const InputLabel = styled.label`
  display: block;
`;

const InputLabelText = styled.span`
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 11px 14px;
  border-radius: 10px;
  font-size: 14px;
  border: 1px solid ${({ $error }) => ($error ? '#ef4444' : '#e5e7eb')};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;

  &:focus {
    ${({ $error }) =>
      $error
        ? ''
        : css`
            border-color: ${BRAND};
            box-shadow: 0 0 0 3px rgba(8, 33, 137, 0.1);
          `}
  }
`;

const InputError = styled.span`
  display: block;
  font-size: 11.5px;
  color: #ef4444;
  margin-top: 4px;
`;

export const Input = ({ label, error, style, ...rest }) => (
  <InputLabel>
    {label ? <InputLabelText>{label}</InputLabelText> : null}
    <StyledInput $error={!!error} style={style} {...rest} />
    {error ? <InputError>{error}</InputError> : null}
  </InputLabel>
);

/* ── Skeleton ── */
const ccShimmer = keyframes`
  0%{background-position:-400px 0}
  100%{background-position:400px 0}
`;

const SkeletonDiv = styled.div`
  height: ${({ $height }) => (typeof $height === 'number' ? `${$height}px` : $height)};
  width: ${({ $width }) => (typeof $width === 'number' ? `${$width}px` : $width)};
  border-radius: ${({ $radius }) => (typeof $radius === 'number' ? `${$radius}px` : $radius)};
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${ccShimmer} 1.4s infinite;
`;

export const Skeleton = ({ height = 16, width = '100%', radius = 8, style }) => (
  <SkeletonDiv $height={height} $width={width} $radius={radius} style={style} />
);

/* ── EmptyState ── */
const EmptyStateWrap = styled.div`
  text-align: center;
  padding: 48px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    padding: 32px 14px;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
`;

const EmptyStateTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #111827;
`;

const EmptyStateSubtitle = styled.div`
  font-size: 13.5px;
  color: #6b7280;
  max-width: 360px;
`;

const EmptyStateAction = styled.div`
  margin-top: 8px;
`;

export const EmptyState = ({ icon = '📭', title, subtitle, action }) => (
  <EmptyStateWrap>
    <EmptyStateIcon>{icon}</EmptyStateIcon>
    <EmptyStateTitle>{title}</EmptyStateTitle>
    {subtitle ? <EmptyStateSubtitle>{subtitle}</EmptyStateSubtitle> : null}
    {action ? <EmptyStateAction>{action}</EmptyStateAction> : null}
  </EmptyStateWrap>
);

/* ── RatingStars ── */
const RatingWrap = styled.span`
  font-size: ${({ $size }) => $size}px;
  color: #f59e0b;
  letter-spacing: 1px;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

export const RatingStars = ({ rating = 0, size = 14, interactive = false, onRate }) => {
  const r = Math.round(rating);
  return (
    <RatingWrap $size={size} $interactive={interactive}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} onClick={interactive ? () => onRate?.(i) : undefined}>{i <= r ? '★' : '☆'}</span>
      ))}
    </RatingWrap>
  );
};

/* ── Modal ── */
const ccModalIn = keyframes`
  from{opacity:0;transform:scale(0.96)}
  to{opacity:1;transform:scale(1)}
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99990;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: ${({ $maxWidth }) => (typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth)};
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  animation: ${ccModalIn} 0.24s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 480px) {
    padding: 20px 16px;
    border-radius: 16px;
    max-width: 100%;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 18px;
  font-size: 19px;
  font-weight: 800;
  color: #111827;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: #f3f4f6;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  color: #6b7280;
  line-height: 1;
`;

export const Modal = ({ open, onClose, children, maxWidth = 480, title }) => {
  if (!open) return null;
  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()} $maxWidth={maxWidth}>
        {title ? <ModalTitle>{title}</ModalTitle> : null}
        <ModalClose onClick={onClose} aria-label="Close">×</ModalClose>
        {children}
      </ModalBox>
    </ModalOverlay>
  );
};
